<script setup lang="ts">
defineProps({
  showLinks: {
    type: Boolean,
    default: true
  }
})

const open = ref(false)

const toggleMenu = () => {
  open.value = !open.value
  const body = document.getElementsByTagName('body')[0]

  if (open.value) body.classList.add('disable-scroll')
  else body.classList.remove('disable-scroll')
}

// to ensure menu closes after navigate in small screens
const clickSmallScreen = () => {
  if (window.innerWidth <= 950) toggleMenu()
}

const lmsDomain = useRuntimeConfig().public.lmsDomain
const jobAdminDomain = useRuntimeConfig().public.jobAdminDomain

const { data: user, status } = useAuth()
</script>

<template>
  <header>
    <div :class="`container ${showLinks ? 'show-links' : 'hide-links'}`">
      <NuxtLink to="/" alt="TalentLabs">
        <img id="navbarLogo" src="/images/landing/talentlabs-logo-thicker.svg" alt="" >
      </NuxtLink>

      <div id="navContainer" :class="`${open ? 'nav-open' : 'nav-close'} hide-scrollbar`">
        <nav @click="clickSmallScreen">
          <div
            v-if="isCourseAdmin(user) || isInstructor(user)"
            class="nav-item nav-item-main clickable"
          >
            <NuxtLink :to="`${lmsDomain}/classroom/course-admin/course-grading`">
              <CommonIconCoursesIconNavGrade /> Grade assignments
            </NuxtLink>
          </div>

          <div
            v-if="isCourseAdmin(user) || isTalentAdmin(user)"
            class="nav-item nav-item-main clickable"
          >
            <NuxtLink :to="`${lmsDomain}/classroom/course-admin/students`">
              <CommonIconJobsIconNavTalent /> Manage talents
            </NuxtLink>
          </div>

          <div
            v-if="isCompanyStaff(user) || isJobAdmin(user)"
            class="nav-item nav-item-main clickable"
          >
            <NuxtLink :to="jobAdminDomain"> <CommonIconJobsIconNavApply /> Manage jobs </NuxtLink>
          </div>

          <div
            v-if="isStudent(user) && !isCompanyStaff(user) && !isInstructor(user)"
            class="nav-item clickable"
          >
            <div class="nav-item-title">
              <span>Learning</span> <CommonIconChevron class="nav-item-chevron" />
            </div>

            <div class="nav-item-list">
              <NuxtLink to="/courses/my-learning">
                <CommonIconCoursesIconNavClassroom />
                My courses
                <CommonSmallNavbarChevron />
              </NuxtLink>

              <NuxtLink to="/courses/browse?page=1" alt="Courses">
                <CommonIconJobsIconNavFind />
                Find courses
                <CommonSmallNavbarChevron />
              </NuxtLink>
            </div>
          </div>

          <div v-if="user" class="nav-item clickable">
            <div class="nav-item-title">
              <span>Jobs</span> <CommonIconChevron class="nav-item-chevron" />
            </div>

            <div class="nav-item-list">
              <NuxtLink
                v-if="isStudent(user) && !isCompanyStaff(user) && !isInstructor(user)"
                to="/jobs/saved-jobs"
                alt="Jobs"
              >
                <CommonIconJobsIconNavSaved /> Saved jobs <CommonSmallNavbarChevron />
              </NuxtLink>
              <NuxtLink
                v-if="isStudent(user) && !isCompanyStaff(user) && !isInstructor(user)"
                to="/jobs/applied-jobs"
                alt="Jobs"
              >
                <CommonIconJobsIconNavApply /> Applied jobs <CommonSmallNavbarChevron />
              </NuxtLink>

              <NuxtLink to="/jobs/browse?page=1" alt="Jobs">
                <CommonIconJobsIconNavFind />
                Find jobs
                <CommonSmallNavbarChevron />
              </NuxtLink>
            </div>
          </div>

          <div
            v-if="isStudent(user) && !isCompanyStaff(user) && !isInstructor(user)"
            class="nav-item clickable"
          >
            <div class="nav-item-title">
              <span>Programmes</span> <CommonIconChevron class="nav-item-chevron" />
            </div>

            <div class="nav-item-list">
              <NuxtLink to="/programmes" alt="Programmes">
                <CommonIconJobsIconNavFind /> Find programmes <CommonSmallNavbarChevron />
              </NuxtLink>
            </div>
          </div>

          <NuxtLink v-if="user" to="/events" class="nav-item nav-item-main clickable">
            <span> Events </span>
            <CommonSmallNavbarChevron />
          </NuxtLink>

          <NuxtLink
            v-if="user"
            :to="isClassAdmin(user) ? '/forum/admin' : '/forum/feed'"
            class="nav-item nav-item-main clickable"
          >
            <span> Forum </span>
            <CommonSmallNavbarChevron />
          </NuxtLink>
        </nav>

        <div id="auth-links" @click="clickSmallScreen">
          <div v-if="status !== 'authenticated'">
            <NuxtLink id="loginButton" to="/login" alt="Login">
              <CommonIconNavbarLogin /> Sign In
            </NuxtLink>
          </div>

          <div v-else id="auth-links-authed">
            <div class="authed-link clickable" @click="navigateTo('/notifications')">
              <CommonIconNotificationsBell />
            </div>

            <AvatarDropDown :user="user" />
          </div>
        </div>
      </div>

      <div id="nav-menu-btns">
        <div class="clickable nav-menu-link" @click="navigateTo('/notifications')">
          <CommonIconNotificationsBell />
        </div>

        <div
          :id="`${open ? 'nav-menu-btn-open' : 'nav-menu-btn-close'}`"
          class="clickable"
          @click="toggleMenu"
        >
          <CommonIconMenu v-if="!open" />
          <CommonIconClose v-if="open" id="close" />
        </div>
      </div>
    </div>
  </header>
</template>

<style scoped lang="scss">
$sidebarWidth: 220px;

header {
  height: var(--navbarHeight);
  width: 100vw;
  /* background: rgba(255, 255, 255, 0.8); */
  background: rgba(255, 255, 255, 1);
  border-bottom: 1px solid rgb(235, 235, 235);
  position: fixed;
  top: 0px;
  left: 0;
  z-index: 100;
  /* backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px); */
}

.container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: auto;
  margin: 0 30px;

  #navbarLogo {
    $margin: 35px;
    width: 120px;
    height: 35px;
    transition: opacity 0.2s ease-out;
    object-position: left;
    object-fit: contain;
    margin-right: $margin;
    padding-right: $margin;
    border-right: 1px solid gainsboro;
  }

  #navContainer {
    display: flex;
    align-items: center;
    flex: 1;
  }

  nav {
    display: flex;
    align-items: center;
    flex: 1;
    height: 100%;
    gap: 40px;
    font-size: 0.93rem;

    .nav-item {
      display: flex;
      align-items: center;
      position: relative;
      padding: 15px 0;

      &-chevron {
        $size: 10px;
        height: $size;
        width: $size;
        transform: rotate(90deg);
        margin-top: 2px;
        margin-left: 5px;
      }

      svg {
        fill: var(--darkBlue);
      }

      &-main {
        svg {
          $size: 13px;
          height: $size;
          width: $size;
          margin-right: 8px;
        }
      }

      &-list {
        border: 1px solid gainsboro;
        position: absolute;
        background-color: white;
        padding: 15px 12px 17px;
        display: flex;
        flex-direction: column;
        top: 40px;
        left: -20px;
        border-radius: 15px;
        z-index: 50;
        box-shadow: 0 10px 30px 10px rgba(0, 0, 0, 0.1);
        gap: 5px;
        display: none;
        min-width: 200px;

        $size: 13px;

        a {
          display: flex;
          align-content: center;
          line-height: $size;
          padding: 15px 30px 15px 12px;
          white-space: nowrap;
        }

        svg {
          height: $size;
          width: $size;
          margin-right: 12px;
          flex-shrink: 0;
        }
      }
    }

    .nav-item:hover {
      .nav-item-list {
        display: flex;
      }
    }
  }

  #auth-links {
    &-authed {
      display: flex;
      align-items: center;
      gap: 20px;

      span {
        font-size: 0.95rem;
        color: var(--textMedium);
      }

      .authed-link {
        $authedSize: 35px;
        height: $authedSize;
        width: $authedSize;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.3s ease-out;

        svg {
          height: 60%;
          width: 60%;
          fill: var(--textM2);
        }
      }
    }

    .auth-links-item {
      $iconSize: 32px;
      height: $iconSize;
      width: $iconSize;
      border: 1px solid rgb(235, 193, 255);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.3s ease-out;

      svg {
        $svgSize: 50%;
        height: $svgSize;
        width: $svgSize;
        fill: #92adc2;
        fill: var(--purple);
        transition: fill 0.3s ease-out;
      }
    }

    a {
      font-weight: 500;
      font-size: 0.95rem;
    }
  }

  #loginButton {
    display: flex;
    gap: 10px;
    color: var(--purple);
    transition: color 0.4s ease-out;

    svg {
      fill: var(--purple);
      transition: fill 0.4s ease-out;
    }
  }

  #nav-menu-btns {
    display: flex;
    align-items: center;
    gap: 10px;

    .nav-menu-link {
      height: 37px;
      width: 37px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: var(--bgMedium);
      border-radius: 50%;
      border: 1px solid var(--bgMedium);
      transition: border 0.3s ease-out;
      display: none;

      svg {
        height: 60%;
        width: 60%;
        fill: var(--textM2);
      }
    }
  }

  #nav-menu-btn-open,
  #nav-menu-btn-close {
    display: none;
    transition:
      border 0.3s ease-out,
      background 0.3s ease-out;
  }
}

header .hide-links {
  #navbarLogo {
    border-right: none;
  }

  nav {
    visibility: hidden;
  }
}

@media (hover: hover) {
  .container {
    nav {
      .nav-item-list {
        a:hover {
          background-color: var(--bgLight);
        }
      }
    }

    #auth-links-authed {
      .authed-link:hover {
        background: var(--bgMedium);
      }
    }
  }
}

@media (max-width: 1100px) {
  .container {
    nav,
    #auth-links {
      gap: 30px;
    }

    $margin: 25px;
  }
}

@mixin menuBtn($border, $bg, $fill) {
  $size: 40px;
  height: $size;
  width: $size;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: $bg;
  border: 1px solid $border;
  position: relative;

  svg {
    $iconSize: 45%;

    height: $iconSize;
    width: $iconSize;
    fill: $fill;
  }

  #close {
    $iconSize: 38%;

    height: $iconSize;
    width: $iconSize;
  }
}

@mixin mainLink() {
  margin: 20px 20px 0;
  width: auto;
  border-radius: 10px;
  padding: 25px 20px;
  font-size: 1rem;
  font-weight: 400;
  border: none;
  background-color: var(--bgMedium);
  display: flex;
  align-items: center;

  span {
    display: block;
    flex: 1;
    font-size: 1rem;
    font-weight: 400;
  }
}

@mixin smallScreen() {
  #navbarLogo {
    width: 90px;
    height: auto;
    padding: 0;
    border-right: 0;
  }

  #nav-menu-btn-close {
    @include menuBtn(transparent, var(--bgMedium), var(--textM2));
  }

  #nav-menu-btn-open {
    @include menuBtn(white, var(--purple), white);
  }

  #navContainer {
    width: 100vw;
    height: dvh;
    top: var(--navbarHeight);
    left: 0;
    position: fixed;
    padding: 0;
    background-color: white;
    transition: height 0.3s ease-out;
    overflow-x: hidden;
    overflow-y: scroll;
    display: flex;
    flex-direction: column;

    nav a::after {
      background: transparent;
    }

    nav {
      display: block;
      margin: 0;
      gap: 0;
      border-right: 0;
      transition: opacity 0.3s ease-out;
      width: 100%;
      height: auto;
      flex: 0;
      padding-top: 20px;

      .nav-item {
        width: 100%;
        display: block;
        padding: 0 20px;
        margin: 0;

        span {
          font-weight: 500;
          flex: 1;
          font-size: 0.9rem;
        }

        svg {
          display: none;
        }

        &-title {
          padding: 20px 0 3px;
          color: var(--textLight);
          margin-bottom: 5px;

          span {
            font-size: 0.85rem;
          }
        }

        &-list {
          display: flex;
          position: relative;
          margin: 0 -20px;
          top: 0;
          left: 0;
          border: none;
          box-shadow: none;
          border-radius: 0;
          padding: 0 20px;

          a {
            padding: 25px 20px;
            background-color: var(--bgLight);
            border-radius: 10px;
            font-size: 1rem;
            display: grid;
            grid-template-columns: 20px 1fr auto;
            gap: 7px;
            align-items: center;

            svg {
              margin: 0;
              display: block;
            }
          }
        }

        &-main {
          @include mainLink();
        }
      }
    }

    #auth-links {
      flex: 1;

      #loginButton {
        margin: 30px auto 40px;
        padding: 15px 15px 15px 10px;
        width: 180px;
        border-radius: 30px;
        font-size: 1rem;
        justify-content: center;
        text-align: center;
        display: inline-flex;
        align-items: center;
        background-color: var(--purple);
        color: white;
        border: 1px solid transparent;
        transition:
          border 0.3s ease-out,
          background 0.3s ease-out;

        svg {
          $size: 16px;
          height: $size;
          width: $size;
          margin-right: 7px;
          fill: white;
        }
      }

      &-authed {
        .authed-link {
          display: none;
        }
      }
    }

    :deep(.avatar-dropdown) {
      width: 100dvw;

      .avatar-dropdown {
        &-avatar {
          display: none;
        }

        &-links {
          display: block;
          position: relative;
          width: 100%;
          top: 0;
          right: 0;
          margin: 0;
          padding: 0;
          box-shadow: none;
          border: none;
          border-radius: 0;

          &-avatar {
            display: none;
          }

          &-list {
            margin: 0;
            gap: 0;
            padding: 0;
            border: none;

            a {
              @include mainLink();
            }

            a > *:first-child {
              display: none;
            }
          }

          &-logout {
            padding: 35px 20px 50px;
            display: flex;
            justify-content: center;

            a {
              padding: 17px 5px;
              width: 100%;
              width: 200px;
              border-radius: 30px;
              justify-content: center;
              color: white;
              background-color: var(--purple);
            }

            svg {
              $logoutSize: 17px;
              height: $logoutSize;
              width: $logoutSize;
              margin-right: 15px;
              fill: white;
            }
          }
        }
      }
    }
  }

  .nav-open {
    height: calc(100dvh - var(--navbarHeight));
    border-top: 1px solid rgb(229, 229, 229);

    nav,
    #auth-links {
      opacity: 1;
    }
  }

  .nav-close {
    height: 0;

    nav,
    #auth-links {
      opacity: 0;
    }
  }
}

@mixin smallScreenHover() {
  #navContainer {
    nav a:hover {
      color: var(--purple);
      background: var(--lightPurple);

      svg {
        fill: var(--purple);
      }
    }

    #auth-links {
      #loginButton:hover {
        border-color: var(--purple);
        color: var(--purple);
        background-color: white;

        svg {
          fill: var(--purple);
        }
      }

      :deep(.avatar-dropdown) {
        .avatar-dropdown-links-list {
          a:hover {
            color: var(--purple);
            background: var(--lightPurple);
          }
        }
      }
    }
  }

  #nav-menu-btns {
    .nav-menu-link:hover {
      border-color: var(--purple);
    }
  }
}

@media (max-width: 1000px) {
  header {
    background: white;

    .hide-links {
      nav {
        visibility: visible;
      }
    }
  }

  .hide-links {
    @include smallScreen();
  }

  @media (hover: hover) {
    .hide-links {
      @include smallScreenHover();
    }
  }
}

@media (max-width: 900px) {
  .container {
    margin: 0 20px;

    #nav-menu-btns {
      .nav-menu-link {
        display: flex;
      }
    }
  }

  .show-links {
    @include smallScreen();
  }

  @media (hover: hover) {
    .show-links {
      @include smallScreenHover();
    }
  }
}

@media (max-width: 600px) {
  header {
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.03);
  }

  .container {
    #navbarLogo {
      width: 95px;
    }

    #nav-menu-btn-open,
    #nav-menu-btn-close {
      $size: 37px;
      height: $size;
      width: $size;
    }
  }
}

@media (hover: hover) {
  .container {
    #navbarLogo:hover {
      opacity: 0.5;
    }

    a:hover::after {
      width: 70%;
    }

    #loginButton:hover {
      color: black;

      svg {
        fill: black;
      }
    }

    #nav-menu-btn-open:hover {
      background-color: var(--black);
    }

    #nav-menu-btn-close:hover {
      border-color: var(--purple);
    }

    #auth-links {
      .auth-links-item:hover {
        background-color: var(--purple);

        svg {
          fill: white;
        }
      }
    }
  }
}
</style>
